// @ts-nocheck
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { splitLine, totalLine } from '../dossier/departmentCounts';
import { useAutosave } from '../../hooks/useAutosave';
import { SaveHint } from '../dossier/fieldKit';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * One Identity P5.2 — Departments (Settings, owner-scoped).
 * Real rows on the department table: create, rename, archive. Renames cascade
 * to the roster so every member's department stays truthful.
 *
 * B4 — the name and the work-type list save as you type, through the shared
 * hook, so this behaves like the context fields and the onboarding cards
 * rather than having its own click-away rule. Archive still needs a click:
 * it takes a department out of routing, and a debounce is the wrong shape for
 * that.
 */

/** One field that writes itself. Reverts and says why when the server refuses,
 *  which a bare onBlur save could not do. */
const InlineAutosaveInput = ({ value, onSave, className, placeholder, onDone }) => {
    const auto = useAutosave({
        value,
        onSave,
        isSavable: (v) => v.trim().length > 0,
    });
    return (
        <span className="flex-1 min-w-0">
            <input
                autoFocus
                value={auto.draft}
                placeholder={placeholder}
                onChange={(e) => auto.set(e.target.value)}
                onBlur={() => { auto.flush(); onDone?.(); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { auto.flush(); onDone?.(); } }}
                className={className}
            />
            <SaveHint state={auto.state} error={auto.error} />
        </span>
    );
};

const DepartmentsTab = ({ clientId }) => {
    const [rows, setRows] = useState([]);
    const [name, setName] = useState('');
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(null);        // department id being renamed
    const [isInternal, setIsInternal] = useState(false);
    const [editingTypes, setEditingTypes] = useState(null); // department id whose work types are open

    const load = async () => {
        try {
            const r = await axios.get(`${API_BASE_URL}/api/workspace/${clientId}/departments`);
            setRows(r.data?.departments || []);
            setIsInternal(!!r.data?.is_internal);
        } catch { toast.error('Could not load departments'); }
    };
    useEffect(() => { if (clientId) load(); }, [clientId]);

    const create = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        setBusy(true);
        try {
            await axios.post(`${API_BASE_URL}/api/workspace/${clientId}/departments`, { name: name.trim() });
            setName(''); load();
            // The shell only offers Company Context once a department exists, so
            // it has to hear about the first one without a reload.
            window.dispatchEvent(new CustomEvent('aai:departments-changed'));
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not create department');
        } finally { setBusy(false); }
    };

    // Rethrows after the toast: the autosave hook reverts the row on a
    // rejection, and the editor may already have closed on blur, so the toast
    // is what the user sees in that case.
    const rename = async (id, next) => {
        try {
            await axios.patch(`${API_BASE_URL}/api/workspace/${clientId}/departments/${id}`, { name: next.trim() });
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not rename');
            throw err;
        }
    };

    // Routing chips (internal company only): which deliverable types this
    // department owns in the Department Loop, e.g. build, skill, document.
    const saveTypes = async (id, raw) => {
        const list = raw.split(',').map((v) => v.trim()).filter(Boolean);
        try {
            await axios.patch(`${API_BASE_URL}/api/workspace/${clientId}/departments/${id}`, { work_types: list });
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not save work types');
            throw err;
        }
    };

    // I4. A department that builds. Its members hold the build permission for
    // as long as they are in it, so moving someone in or out is the whole admin
    // action; there is nothing else to grant or revoke. Owner-gated on the
    // server (aai_manage_departments), same as who heads a department.
    const toggleTechnical = async (row) => {
        try {
            await axios.patch(`${API_BASE_URL}/api/workspace/${clientId}/departments/${row.id}`,
                { is_technical: !row.is_technical });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not change that');
        }
    };

    const toggleArchive = async (row) => {
        try {
            await axios.patch(`${API_BASE_URL}/api/workspace/${clientId}/departments/${row.id}`, { archived: !row.archived });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not update');
        }
    };

    return (
        <div className="bg-white rounded-panel border border-line p-6">
            <h2 className="text-[20px] font-bold leading-[28px] tracking-[-0.005em] text-ink mb-1">Departments</h2>
            <p className="text-sm text-ink-dim mb-4">
                Structure your team. Members are placed into a department when you invite
                or enroll them; department heads see their own department's progress. Mark a
                department as one that builds and its people can start a build themselves
                instead of requesting one.
                {isInternal && ' This is the internal company: each department\'s work types decide which requests route to it.'}
            </p>

            <div className="space-y-2 mb-5">
                {rows.length === 0 && (
                    <p className="text-sm text-ink-soft py-4 text-center">No departments yet — add your first below.</p>
                )}
                {rows.map((d) => (
                    <div key={d.id} className={`flex items-center gap-3 border border-line-soft rounded-lg px-4 py-2.5 ${d.archived ? 'opacity-50' : ''}`}>
                        {editing === d.id ? (
                            <InlineAutosaveInput
                                value={d.name}
                                onSave={(next) => rename(d.id, next)}
                                onDone={() => setEditing(null)}
                                className="w-full border border-line-strong rounded px-2 py-1 text-sm" />
                        ) : (
                            <button className="text-sm font-medium text-ink flex-1 text-left"
                                onClick={() => !d.archived && setEditing(d.id)}
                                title="Click to rename">
                                {d.name}
                            </button>
                        )}
                        <span className="flex flex-col text-xs text-ink-soft">
                            <span>{totalLine(d)}</span>
                            {splitLine(d) && <span className="text-[11px]">{splitLine(d)}</span>}
                        </span>
                        {!d.archived && (
                            <button onClick={() => toggleTechnical(d)}
                                title={d.is_technical
                                    ? 'People here can start a build themselves. Click to turn that off.'
                                    : 'Let people here start a build themselves, instead of requesting one.'}
                                className={`text-[11px] font-semibold rounded-full px-2.5 py-1 transition-colors ${
                                    d.is_technical
                                        ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                                        : 'bg-canvas text-ink-dim hover:bg-canvas-inset'
                                }`}>
                                {d.is_technical ? 'Builds things' : 'Asks first'}
                            </button>
                        )}
                        <button onClick={() => toggleArchive(d)}
                            className="text-xs text-ink-dim hover:text-ink">
                            {d.archived ? 'Restore' : 'Archive'}
                        </button>
                    </div>
                ))}
                {isInternal && rows.filter((d) => !d.archived).map((d) => (
                    <div key={`wt-${d.id}`} className="flex items-center gap-2 pl-4 -mt-1 pb-1">
                        <span className="text-[10.5px] font-sans uppercase tracking-wider text-ink-soft shrink-0">{d.name} owns</span>
                        {editingTypes === d.id ? (
                            <InlineAutosaveInput
                                value={(d.work_types || []).join(', ')}
                                onSave={(raw) => saveTypes(d.id, raw)}
                                onDone={() => setEditingTypes(null)}
                                placeholder="build, skill, document"
                                className="w-full max-w-xs border border-line-strong rounded px-2 py-0.5 text-xs" />
                        ) : (
                            <button
                                onClick={() => setEditingTypes(d.id)}
                                className="flex items-center gap-1.5 flex-wrap text-left"
                                title="Click to edit work types">
                                {(d.work_types || []).length === 0 ? (
                                    <span className="text-xs text-amber-600">no work types — this department receives nothing</span>
                                ) : (
                                    (d.work_types || []).map((t) => (
                                        <span key={t} className="text-[11px] font-sans bg-brand-50 text-brand-700 rounded-full px-2 py-0.5">{t}</span>
                                    ))
                                )}
                            </button>
                        )}
                    </div>
                ))}
            </div>

            <form onSubmit={create} className="flex gap-2">
                <input value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="New department (e.g. Sales)"
                    className="border border-line-strong rounded-lg px-3 py-2 text-sm flex-1 max-w-xs" />
                <button type="submit" disabled={busy}
                    className="bg-brand-500 text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-60">
                    Add
                </button>
            </form>
        </div>
    );
};

export default DepartmentsTab;
